import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { PlayWrapper } from './Play.style';
// import PlayPitches from './play/PlayPitches';
import PlayResults from './play/PlayResults';
import PlayScores from './play/PlayScores';
import PlayEvents, { PlayEventTypes } from './play/PlayEvents';

const TOP = 'top';

const {
    ACTION,
    PICKOFF,
    STEPOFF
} = PlayEventTypes;

const generateBaseResults = (keyMomentData, keyMomentDataExists, matchup) => {
    if (keyMomentDataExists) {
        const {
            offense = {}
        } = keyMomentData;
        const {
            first,
            second,
            third
        } = offense;
        return {
            first: !!first,
            second: !!second,
            third: !!third
        };
    } else {
        const {
            postOnFirst,
            postOnSecond,
            postOnThird
        } = matchup;
        return {
            first: !!postOnFirst,
            second: !!postOnSecond,
            third: !!postOnThird
        };
    }
};

// Build the total number of segments of the play to display
const buildPlaySegments = (
    playData,
    teams,
    offset,
    homeNameFallback,
    awayNameFallback,
    resultVideo,
    keymoments = {},
    isLoading,
    language,
    deviceInteraction,
    openAtBatOverlay
) => {
    const {
        playEvents = [],
        about = {},
        result = {},
        matchup = {},
        atBatIndex
    } = playData;

    const {
        isScoringPlay
    } = about;

    const showKeyMoment = !!keymoments;
    const keyMoment = (showKeyMoment && keymoments[atBatIndex]) || {};
    const keyMomentCount = showKeyMoment ? keyMoment.preCount || {} : {};

    const {
        batter = {}
    } = matchup;

    const playSegments = [];

    const universalPayload = {
        offset
    };

    const basesResults = generateBaseResults(keyMoment, showKeyMoment, matchup);

    let index = 0;

    // Calculate play segments from furthest past play to most current
    // Will reverse sort in Play return func if doing reverse chron.
    for (const event of playEvents) {
        const {
            details = {},
            type,
            isBaseRunningPlay,
            hitData,
            count: eventCount
        } = event;

        const count = showKeyMoment ? keyMomentCount : eventCount;

        // If this event is an action or pickoff, send data to PlayEvent for further
        // processing
        const showEvent = isBaseRunningPlay || !showKeyMoment; // Will only show on KeyMoments if something worthwhile happened.
        if (showEvent && (type === ACTION || type === PICKOFF || type === STEPOFF)) {
            playSegments.push(
                <PlayEvents
                    playEventType={ type }
                    isBaseRunningPlay={ isBaseRunningPlay }
                    data={ event }
                    key={ `playEvent-${index}` }
                    outCount={ type === PICKOFF ? count.outs : undefined }
                    count={ count }
                    isLoading={ isLoading }
                    { ...universalPayload }
                />
            );
        }

        if (details.isScoringPlay) {
            const {
                homeScore,
                awayScore
            } = details;

            playSegments.push(
                <PlayScores
                    teams={ teams }
                    isTopInning={ about.halfInning === TOP }
                    homeScore={ homeScore }
                    awayScore={ awayScore }
                    key={ `playScores-${index}` }
                    homeNameFallback={ homeNameFallback }
                    awayNameFallback={ awayNameFallback }
                    isLoading={isLoading}
                    { ...universalPayload }
                />
            );
        }

        if (index === playEvents.length - 1 && result) {
            const playResultAddtionalProps = keymoments
                ? {
                    keymoments: keymoments[atBatIndex],
                    halfInning: about?.halfInning,
                    awayTeamName: teams?.away?.team?.teamName,
                    homeTeamName: teams?.home?.team?.teamName
                }
                : {};

            playSegments.push(
                <PlayResults
                    key={ `playResult-${index}` }
                    result={ result }
                    outCount={ count.outs }
                    batterId={ batter.id }
                    hitData={ hitData }
                    basesResults={ basesResults }
                    resultVideo={ resultVideo }
                    count={ count }
                    isLoading={isLoading}
                    language={ language }
                    deviceInteraction={ deviceInteraction }
                    { ...playResultAddtionalProps }
                    { ...universalPayload }
                    openAtBatOverlay={openAtBatOverlay}
                    atBatIndex={atBatIndex}
                />
            );
        }
        index++;
    }
    if (isScoringPlay) {
        const {
            homeScore,
            awayScore
        } = result;

        playSegments.push(
            <PlayScores
                teams={ teams }
                isTopInning={ about.halfInning === TOP }
                homeScore={ homeScore }
                awayScore={ awayScore }
                key={ `playScores-${index}` }
                homeNameFallback={ homeNameFallback }
                awayNameFallback={ awayNameFallback }
                isLoading={isLoading}
                { ...universalPayload }
            />
        );
    }

    return playSegments;
};

const Play = ({
    playData,
    teams,
    offset = false,
    homeNameFallback = 'HOME',
    awayNameFallback = 'AWAY',
    reverseChron = false,
    resultVideo,
    keymoments,
    isLoading,
    language,
    deviceInteraction = {},
    openAtBatOverlay
}) => {
    const playSegments = useMemo(() => buildPlaySegments(
        playData,
        teams,
        offset,
        homeNameFallback,
        awayNameFallback,
        resultVideo,
        keymoments,
        isLoading,
        language,
        deviceInteraction,
        openAtBatOverlay
    ), [playData, isLoading]);

    const orderedPlaySegments = reverseChron ? [...playSegments].reverse() : playSegments;

    // TODO: Maybe switch PlayWrapper to React.Fragment if we want each of the potential subcomponents
    // to be on the same hierarchical level
    return (
        <PlayWrapper>
            { orderedPlaySegments }
        </PlayWrapper>
    );
};

Play.propTypes = {
    playData: PropTypes.object.isRequired,
    teams: PropTypes.object.isRequired,
    offset: PropTypes.bool,
    homeNameFallback: PropTypes.string,
    awayNameFallback: PropTypes.string,
    reverseChron: PropTypes.bool,
    resultVideo: PropTypes.object,
    keymoments: PropTypes.object,
    isLoading: PropTypes.bool,
    deviceInteraction: PropTypes.object,
    language: PropTypes.string
};

export default Play;
